import { useMemo, useState } from "react";
import { styles } from "../styles";
import { hasCapability } from "../utils/capabilities";
import {
  computeLegacyJobTotal,
  isEffectivelyTaxExempt,
} from "../utils/billTotals";
import BackButton from "../components/BackButton";
import AgingSummary from "../components/AgingSummary";
import {
  AGING_BUCKETS,
  bucketForDays,
  daysPastDue,
  effectiveDueDate,
  summarizeByCustomer,
} from "../utils/aging";
import {
  exportPdf,
  printPdf,
  shouldShowPdfChooser,
} from "../utils/pdfExport";
import PdfActionModal from "../components/PdfActionModal";
import PaymentEntryModal from "../components/PaymentEntryModal";
import { buildInvoicePdfDoc } from "../utils/invoicePdf";
import {
  buildInvoiceOfficeCopyDoc,
  costingForBill,
} from "../utils/invoiceOfficeCopyPdf";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

// computeLegacyJobTotal moved to src/utils/billTotals.js so the
// dashboard Invoices mini-view can compute the same number. Same
// math, just hoisted to share.

// Customer payment-history summary block (Phase C). Collapsed by default
// so it doesn't dominate the page. Click to expand → table of every
// customer with billed / paid / outstanding / paidYtd / paid-late count.
// Click a row to filter the invoice list to that customer.
function CustomerHistoryBlock({
  records,
  showCustomerHistory,
  setShowCustomerHistory,
  onCustomerClick,
}) {
  const rows = useMemo(() => {
    const lookup = (id) =>
      records.find((r) => r.customerId === id)?.customerName || "(unknown)";
    return summarizeByCustomer(
      records.filter((r) => !r.voided),
      lookup
    );
  }, [records]);

  if (rows.length === 0) return null;

  return (
    <div
      style={{
        marginBottom: "12px",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        background: "#f9fafb",
      }}
    >
      <div
        onClick={() => setShowCustomerHistory((s) => !s)}
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>
          {showCustomerHistory ? "▾" : "▸"} Customer payment history (
          {rows.length})
        </span>
        <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 400 }}>
          Click row to filter
        </span>
      </div>
      {showCustomerHistory && (
        <div
          style={{
            overflowX: "auto",
            padding: "0 12px 12px",
          }}
        >
          <table
            style={{
              width: "100%",
              fontSize: "12px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr
                style={{
                  color: "#6b7280",
                  textAlign: "left",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <th style={{ padding: "4px 8px" }}>Customer</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>
                  Bills
                </th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>
                  Total billed
                </th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>
                  Total paid
                </th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>
                  Paid YTD
                </th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>
                  Outstanding
                </th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>
                  Paid late
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.customerId}
                  onClick={() =>
                    onCustomerClick && r.customerId && onCustomerClick(r.customerId)
                  }
                  style={{
                    cursor: r.customerId ? "pointer" : "default",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <td style={{ padding: "4px 8px", fontWeight: 600 }}>
                    {r.customerName}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {r.billsCount}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {(r.totalBilled || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {(r.totalPaid || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {(r.paidYtd || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                  <td
                    style={{
                      padding: "4px 8px",
                      textAlign: "right",
                      color: r.outstanding > 0.005 ? "#b91c1c" : "#1a7f37",
                      fontWeight: r.outstanding > 0.005 ? 600 : 400,
                    }}
                  >
                    {(r.outstanding || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                  <td
                    style={{
                      padding: "4px 8px",
                      textAlign: "right",
                      color: r.paidLateCount > 0 ? "#92400e" : "#6b7280",
                    }}
                  >
                    {r.paidLateCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Searchable / filterable list of every invoice this contractor org
 * has issued across every job. Flattens two shapes into one record list:
 *   - Multi-bill jobs (job.bills[]): one record per bill (partial / final).
 *   - Legacy single-finalize jobs (job.billFinalized && no bills[]): one
 *     record per job with totals computed from costing.
 *
 * Each row exposes Export PDF (uses the shared buildInvoicePdfDoc helper
 * so the contractor's per-bill print matches the facility's view) +
 * View Job (jumps to the underlying JobDetail page).
 */
export default function InvoicesPage({
  jobsRaw = [],
  publicInvoicesRaw = [],
  customers = [],
  role,
  capabilities,
  user,
  onSelectJob,
  onBack,
  orgName = "",
  orgAddress = {},
  logoUrl = "",
  actions = null,
}) {
  // Dedicated viewInvoices cap (Phase C+ split — was previously
  // piggy-backed on editJobs, but invoices are a billing concern, not
  // a job-ops one). Billing dept preset enables it; admin always sees
  // via role override.
  const canSeeInvoices = hasCapability(role, capabilities, "viewInvoices");
  const canViewAging = hasCapability(role, capabilities, "viewARAging");
  const canViewHistory = hasCapability(
    role,
    capabilities,
    "viewPaymentHistory"
  );
  const canMarkPaid = hasCapability(role, capabilities, "markPaid");
  const canSeeMargin = hasCapability(role, capabilities, "viewMargin");
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [ackFilter, setAckFilter] = useState(""); // "" | "pending" | "acknowledged"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Phase C filter: payment status / aging bucket. "" = all,
  // "outstanding" = paid status != paid AND not voided, "past_due" =
  // outstanding AND days past due > 0, or one of AGING_BUCKETS keys.
  const [payStatusFilter, setPayStatusFilter] = useState("");
  const [pdfPending, setPdfPending] = useState(null);
  const [showCustomerHistory, setShowCustomerHistory] = useState(false);

  // Cross-reference each invoice record with its publicInvoice mirror doc
  // to surface ack status the facility set. Keyed by id (which matches
  // `${jobId}_${billId}` for multi-bill and `${jobId}` for legacy).
  const publicById = useMemo(() => {
    const m = new Map();
    for (const p of publicInvoicesRaw) m.set(p.id, p);
    return m;
  }, [publicInvoicesRaw]);

  // Resolve customer + location once per row. Keyed by customer id so the
  // memo is stable across re-renders even when the customers array
  // reference is recreated by useOrgData.
  const customerById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(c.id, c);
    return m;
  }, [customers]);

  const records = useMemo(() => {
    const out = [];
    for (const job of jobsRaw) {
      const customer = customerById.get(job.customerId);
      const location = (customer?.locations || []).find(
        (l) => l.id === job.locationId
      );
      const customerName = customer?.name || "—";
      const locationName = location?.name || "—";
      const billsList = job.bills || [];

      if (billsList.length > 0) {
        // Multi-bill (Progress Bill flow). One record per bill.
        billsList.forEach((bill, idx) => {
          const docId = `${job.id}_${bill.id}`;
          const pub = publicById.get(docId);
          out.push({
            id: docId,
            jobId: job.id,
            billId: bill.id,
            billIndex: idx,
            job,
            customer,
            customerId: customer?.id || "",
            location,
            customerName,
            locationName,
            jobNumber: job.jobNumber || "",
            jobTitle: job.title || "",
            invoiceNumber: bill.invoiceNumber || "",
            description: bill.description || "",
            type: bill.type || "partial",
            finalizedAt: bill.finalizedAt || "",
            subtotal: bill.subtotal || 0,
            taxes: bill.taxes || [],
            grandTotal: bill.grandTotal || 0,
            acknowledged: !!pub?.acknowledged,
            acknowledgedAt: pub?.acknowledgedAt || "",
            acknowledgedBy: pub?.acknowledgedBy || "",
            // Phase A: voided fields. Phase B: payment fields. Phase C
            // adds dueDate (set at finalize time post-Phase-C; falls back
            // to finalizedAt+30 via aging helper for older bills).
            voided: !!bill.voided,
            voidedAt: bill.voidedAt || "",
            voidReason: bill.voidReason || "",
            dueDate: bill.dueDate || "",
            paidAmount: bill.paidAmount || 0,
            paymentStatus: bill.paymentStatus || "unpaid",
            payments: bill.payments || [],
            lastPaymentAt: bill.lastPaymentAt || null,
          });
        });
      } else if (job.billFinalized) {
        // Legacy single-bill (today's default for one-shot T&M jobs).
        const totals = computeLegacyJobTotal(job, customer);
        const pub = publicById.get(job.id);
        out.push({
          id: job.id,
          jobId: job.id,
          billId: null,
          job,
          customer,
          customerId: customer?.id || "",
          location,
          customerName,
          locationName,
          jobNumber: job.jobNumber || "",
          jobTitle: job.title || "",
          invoiceNumber: job.invoiceNumber || "",
          description:
            job.costing?.billDescription || job.description || "",
          type: "single",
          finalizedAt: job.billFinalizedAt || "",
          subtotal: totals.subtotal,
          taxes: totals.taxes,
          grandTotal: totals.grandTotal,
          acknowledged: !!pub?.acknowledged,
          acknowledgedAt: pub?.acknowledgedAt || "",
          acknowledgedBy: pub?.acknowledgedBy || "",
          // Legacy single-bills predate the void/paid Phase A+B fields.
          // recordBillPayment now seeds these on the job root the first
          // time a payment is recorded; missing fields default to safe
          // zeros so aging math doesn't blow up on pre-payment bills.
          voided: false,
          voidedAt: "",
          voidReason: "",
          dueDate: "",
          paidAmount: job.paidAmount || 0,
          paymentStatus: job.paymentStatus || "unpaid",
          payments: job.payments || [],
          lastPaymentAt: job.lastPaymentAt || null,
        });
      }
    }
    return out;
  }, [jobsRaw, customerById, publicById]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return records.filter((r) => {
      if (customerFilter && r.customer?.id !== customerFilter) return false;
      if (typeFilter && r.type !== typeFilter) return false;
      if (ackFilter === "pending" && r.acknowledged) return false;
      if (ackFilter === "acknowledged" && !r.acknowledged) return false;
      // Phase C pay-status / aging filter.
      if (payStatusFilter) {
        if (r.voided) return false;
        const outstanding =
          Math.max(0, (r.grandTotal || 0) - (r.paidAmount || 0)) > 0.005;
        if (payStatusFilter === "outstanding" && !outstanding) return false;
        if (payStatusFilter === "paid" && outstanding) return false;
        if (payStatusFilter === "past_due") {
          if (!outstanding) return false;
          const due = effectiveDueDate(r);
          const days = daysPastDue(due);
          if (!days || days <= 0) return false;
        }
        // Specific aging bucket filter (current / d1_30 / d31_60 / etc.).
        const bucketKeys = AGING_BUCKETS.map((b) => b.key);
        if (bucketKeys.includes(payStatusFilter)) {
          if (!outstanding) return false;
          const due = effectiveDueDate(r);
          const days = daysPastDue(due);
          if (bucketForDays(days) !== payStatusFilter) return false;
        }
      }
      if (dateFrom && (r.finalizedAt || "") < dateFrom) return false;
      if (dateTo && (r.finalizedAt || "") > `${dateTo}T23:59:59.999Z`)
        return false;
      if (term) {
        const hay = [
          r.invoiceNumber,
          r.jobNumber,
          r.jobTitle,
          r.description,
          r.customerName,
          r.locationName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [
    records,
    search,
    customerFilter,
    typeFilter,
    ackFilter,
    payStatusFilter,
    dateFrom,
    dateTo,
  ]);

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) =>
        (b.finalizedAt || "").localeCompare(a.finalizedAt || "")
      ),
    [filtered]
  );

  const total = sorted.reduce((s, r) => s + (r.grandTotal || 0), 0);

  // CSV download for bookkeeper / QuickBooks bulk import. Exports
  // whatever the current search + filters are showing -- if they
  // narrowed to "unpaid, July, Customer X" the CSV reflects that.
  // Columns picked to align with QBO Invoice import field names so
  // a bookkeeper can map directly without renaming.
  const handleExportCsv = () => {
    const escape = (v) => {
      const s = String(v ?? "");
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "");
    const taxTotalOf = (r) =>
      (r.taxes || []).reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const balanceOf = (r) =>
      Math.max(0, (r.grandTotal || 0) - (r.paidAmount || 0));

    const header = [
      "Invoice #",
      "Invoice Date",
      "Due Date",
      "Customer",
      "Location / Job Site",
      "Job #",
      "Description",
      "Type",
      "Subtotal",
      "Tax",
      "Grand Total",
      "Paid Amount",
      "Balance Due",
      "Payment Status",
      "Last Payment Date",
      "Voided",
      "Void Reason",
      "Acknowledged",
    ];
    const rows = sorted.map((r) => [
      r.invoiceNumber || "",
      fmtDate(r.finalizedAt),
      fmtDate(r.dueDate),
      r.customerName || "",
      r.locationName || "",
      r.jobNumber || "",
      r.description || r.jobTitle || "",
      r.type || "",
      (r.subtotal || 0).toFixed(2),
      taxTotalOf(r).toFixed(2),
      (r.grandTotal || 0).toFixed(2),
      (r.paidAmount || 0).toFixed(2),
      balanceOf(r).toFixed(2),
      r.paymentStatus || "unpaid",
      r.lastPaymentAt ? fmtDate(r.lastPaymentAt) : "",
      r.voided ? "Yes" : "",
      r.voidReason || "",
      r.acknowledged ? "Yes" : "",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(escape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build the payload buildInvoicePdfDoc wants for a single record.
  // Extracted from exportInvoice so the bulk-export handler reuses
  // the same shape -- one source of truth for invoice PDF data.
  const invoicePayload = (r) => {
    let priorBills = [];
    if (r.billId) {
      const all = r.job.bills || [];
      priorBills = all.slice(0, r.billIndex).map((b) => ({
        invoiceNumber: b.invoiceNumber || "",
        description: b.description || "",
        grandTotal: b.grandTotal || 0,
        finalizedAt: b.finalizedAt || "",
        type: b.type || "partial",
      }));
    }
    return {
      orgName,
      orgAddress,
      logoUrl,
      invoiceNumber: r.invoiceNumber,
      jobNumber: r.jobNumber,
      jobTitle: r.jobTitle,
      jobType: r.job.jobType || "",
      date: r.finalizedAt ? new Date(r.finalizedAt) : new Date(),
      customerName: r.customer?.name || "—",
      customerBilling: {
        street: r.customer?.billingStreet,
        city: r.customer?.billingCity,
        state: r.customer?.billingState,
        zip: r.customer?.billingZip,
      },
      locationName: r.location?.name || "—",
      locationAddress: {
        street: r.location?.street,
        suite: r.location?.suite || r.location?.building,
        city: r.location?.city,
        state: r.location?.state,
        zip: r.location?.zip,
      },
      workDescription: r.description,
      subtotal: r.subtotal,
      taxes: r.taxes,
      grandTotal: r.grandTotal,
      // Prefer the legacy bill's finalize-time snapshot when it exists
      // (pre-QB #6). Multi-bill rows already carry per-bill stamped totals
      // so they're inherently immutable — only the legacy single-finalize
      // path needed this snapshot. Falls back to live EFFECTIVE exempt
      // (taxExempt AND cert not expired) for drafts and pre-snapshot
      // bills — expired cert means tax applies until renewal.
      taxExempt:
        r.billId == null &&
        typeof r.job.billTaxExemptAtFinalize === "boolean"
          ? r.job.billTaxExemptAtFinalize
          : isEffectivelyTaxExempt(r.customer),
      taxExemptCert:
        r.billId == null && r.job.billTaxExemptCertAtFinalize
          ? r.job.billTaxExemptCertAtFinalize
          : r.customer?.taxExemptCert || "",
      priorBills,
      finalizedAt: r.finalizedAt,
    };
  };

  // Bulk PDF export -- bundles every invoice in the current filter
  // into one multi-page PDF. Page 1 = first invoice; jsPDF's addPage
  // (via buildInvoicePdfDoc's optional doc param) chains the rest.
  // Bookkeeper gets one file to email / print instead of N downloads.
  const handleBundlePdf = async () => {
    if (sorted.length === 0) return;
    if (
      sorted.length > 50 &&
      !window.confirm(
        `Bundle ${sorted.length} invoices into one PDF? This may take a moment.`
      )
    ) {
      return;
    }
    let doc = null;
    for (const r of sorted) {
      doc = await buildInvoicePdfDoc({ ...invoicePayload(r), doc });
    }
    if (!doc) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `invoices-bundle-${sorted.length}-${stamp}.pdf`;
    await exportPdf(doc, filename);
  };

  const exportInvoice = async (r) => {
    const doc = await buildInvoicePdfDoc(invoicePayload(r));
    const dateSlug = (r.finalizedAt || new Date().toISOString()).slice(
      0,
      10
    );
    const filename = `Invoice_${r.invoiceNumber || r.jobNumber || r.jobId}_${dateSlug}.pdf`;
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  // Internal Office Copy print — admin-only via canSeeMargin. Watermarked
  // OFFICE COPY, shows cost / charge / margin per line. Mirrors the quote
  // office copy so the bookkeeper has a consistent review surface across
  // the quote AND invoice pipelines.
  const exportOfficeCopy = async (r) => {
    const bill = r.billId
      ? (r.job.bills || []).find((b) => b.id === r.billId)
      : null;
    // Snapshot-aware exempt status (pre-QB #6) — same rule as the
    // customer PDF payload above. Legacy bill: prefer snapshot; multi-bill
    // and drafts: live customer.
    const exemptForBill =
      r.billId == null &&
      typeof r.job.billTaxExemptAtFinalize === "boolean"
        ? r.job.billTaxExemptAtFinalize
        : isEffectivelyTaxExempt(r.customer);
    const exemptCertForBill =
      r.billId == null && r.job.billTaxExemptCertAtFinalize
        ? r.job.billTaxExemptCertAtFinalize
        : r.customer?.taxExemptCert || "";
    const doc = await buildInvoiceOfficeCopyDoc({
      orgName,
      orgAddress,
      logoUrl,
      invoiceNumber: r.invoiceNumber,
      jobNumber: r.jobNumber,
      jobTitle: r.jobTitle,
      customerName: r.customer?.name || "-",
      locationName: r.location?.name || "",
      costing: costingForBill(r.job, bill),
      taxExempt: exemptForBill,
      taxExemptCert: exemptCertForBill,
      finalizedAt: r.finalizedAt,
    });
    const dateSlug = (r.finalizedAt || new Date().toISOString()).slice(0, 10);
    const filename = `Invoice_${
      r.invoiceNumber || r.jobNumber || r.jobId
    }_OfficeCopy_${dateSlug}.pdf`;
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  if (!canSeeInvoices) {
    return (
      <>
        <BackButton onClick={onBack} />
        <h2>Invoices</h2>
        <p style={{ color: "#888" }}>
          You don't have permission to view invoices for this org.
        </p>
      </>
    );
  }

  return (
    <>
      <BackButton onClick={onBack} />
      <h2 style={{ margin: "8px 0 4px" }}>Invoices</h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
        {sorted.length} invoice{sorted.length === 1 ? "" : "s"} ·{" "}
        Total: {money(total)}
      </div>

      {canViewAging && (
        <AgingSummary
          title="AR Aging"
          bills={records.filter((r) => !r.voided)}
          onBucketClick={(key) => setPayStatusFilter(key)}
        />
      )}

      {canViewHistory && (
        <CustomerHistoryBlock
          records={records}
          showCustomerHistory={showCustomerHistory}
          setShowCustomerHistory={setShowCustomerHistory}
          onCustomerClick={(custId) => setCustomerFilter(custId)}
        />
      )}

      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          marginBottom: "12px",
        }}
      >
        <input
          type="text"
          placeholder="Search inv# / job# / customer / building / description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, minWidth: "260px", flex: 1 }}
        />
        <select
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
        >
          <option value="">All customers</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          title="Filter by invoice type"
        >
          <option value="">All types</option>
          <option value="single">Single bill</option>
          <option value="partial">Partial</option>
          <option value="final">Final</option>
        </select>
        <select
          value={ackFilter}
          onChange={(e) => setAckFilter(e.target.value)}
          title="Filter by whether the facility has acknowledged the invoice"
        >
          <option value="">All ack status</option>
          <option value="pending">Awaiting acknowledgement</option>
          <option value="acknowledged">Acknowledged</option>
        </select>
        <select
          value={payStatusFilter}
          onChange={(e) => setPayStatusFilter(e.target.value)}
          title="Filter by payment status / aging bucket"
        >
          <option value="">All payment status</option>
          <option value="outstanding">Outstanding (unpaid + partial)</option>
          <option value="past_due">Past due</option>
          <option value="paid">Paid in full</option>
          {AGING_BUCKETS.map((b) => (
            <option key={b.key} value={b.key}>
              Aging: {b.label}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          title="From"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          title="To"
        />
        <button
          type="button"
          style={{ ...styles.button, margin: 0 }}
          onClick={handleExportCsv}
          disabled={sorted.length === 0}
          title="Download the currently-filtered invoice list as CSV. Use this to bulk-import to QuickBooks or hand off to your bookkeeper."
        >
          Download CSV ({sorted.length})
        </button>
        <button
          type="button"
          style={{ ...styles.button, margin: 0 }}
          onClick={handleBundlePdf}
          disabled={sorted.length === 0}
          title="Bundle every invoice in the current filter into one multi-page PDF. Good for batch email or print."
        >
          Bundle PDF ({sorted.length})
        </button>
      </div>

      {sorted.length === 0 ? (
        <div
          style={{
            padding: "16px",
            color: "#888",
            border: "1px dashed #ddd",
            borderRadius: "6px",
          }}
        >
          {records.length === 0
            ? "No invoices yet. Once you finalize a bill on a job, it shows up here."
            : "No invoices match the current filters. Adjust search / filters above."}
        </div>
      ) : (
        <div style={styles.card}>
          {sorted.map((r) => {
            const typeStyle =
              r.type === "final"
                ? { background: "#c9efc9", color: "#1a5f1f" }
                : r.type === "partial"
                ? { background: "#fff3cd", color: "#7a5400" }
                : { background: "#e0e9f7", color: "#1a5fb4" };
            const showRecordPayment =
              canMarkPaid && !r.voided && r.paymentStatus !== "paid";
            return (
              <div
                key={r.id}
                style={{
                  borderTop: "1px solid #eee",
                  padding: "10px 0",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: "240px" }}>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>
                    {r.invoiceNumber ||
                      (r.jobNumber ? `Job #${r.jobNumber}` : "Invoice")}
                    {r.invoiceNumber && r.jobNumber && (
                      <span
                        style={{
                          color: "#666",
                          fontSize: "12px",
                          fontWeight: 400,
                          marginLeft: "6px",
                        }}
                      >
                        · Job #{r.jobNumber}
                      </span>
                    )}
                    {r.jobTitle && (
                      <span
                        style={{
                          color: "#444",
                          fontSize: "13px",
                          fontWeight: 400,
                          marginLeft: "6px",
                        }}
                      >
                        — {r.jobTitle}
                      </span>
                    )}
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "10px",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        textTransform: "uppercase",
                        ...typeStyle,
                      }}
                    >
                      {r.type}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {r.customerName} · {r.locationName}
                    {r.finalizedAt &&
                      ` · ${new Date(r.finalizedAt).toLocaleDateString()}`}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#1a5fb4",
                    minWidth: "90px",
                    textAlign: "right",
                  }}
                >
                  {money(r.grandTotal)}
                </div>
                {/* Phase C: pay-status + aging badge alongside ack status. */}
                {!r.voided && (() => {
                  const remaining = Math.max(
                    0,
                    (r.grandTotal || 0) - (r.paidAmount || 0)
                  );
                  const status =
                    remaining < 0.005
                      ? "paid"
                      : r.paidAmount > 0
                      ? "partial"
                      : "unpaid";
                  const due = effectiveDueDate(r);
                  const days = daysPastDue(due);
                  const bucket = bucketForDays(days);
                  const palette = AGING_BUCKETS.find((b) => b.key === bucket);
                  return (
                    <span
                      style={{
                        fontSize: "11px",
                        padding: "3px 8px",
                        background:
                          status === "paid" ? "#d4edda" : palette?.bg,
                        color:
                          status === "paid" ? "#1a5f1f" : palette?.color,
                        border:
                          status === "paid"
                            ? "1px solid #a7e3b3"
                            : `1px solid ${palette?.color}40`,
                        borderRadius: "3px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        marginLeft: "4px",
                      }}
                      title={
                        status === "paid"
                          ? "Paid in full"
                          : `${money(remaining)} outstanding${
                              due ? ` · due ${due}` : ""
                            }${
                              days && days > 0
                                ? ` · ${days} day${days === 1 ? "" : "s"} past due`
                                : ""
                            }`
                      }
                    >
                      {status === "paid"
                        ? "Paid"
                        : status === "partial"
                        ? `Partial ${money(remaining)}`
                        : days > 0
                        ? `${days}d past due`
                        : "Unpaid"}
                    </span>
                  );
                })()}
                {r.acknowledged ? (
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "3px 8px",
                      background: "#d4edda",
                      color: "#1a5f1f",
                      borderRadius: "3px",
                      fontWeight: 600,
                    }}
                    title={
                      r.acknowledgedAt
                        ? `Acknowledged ${new Date(
                            r.acknowledgedAt
                          ).toLocaleString()}${
                            r.acknowledgedBy
                              ? ` by ${r.acknowledgedBy}`
                              : ""
                          }`
                        : "Acknowledged by facility"
                    }
                  >
                    Acknowledged
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "3px 8px",
                      background: "#fff3cd",
                      color: "#7a5400",
                      borderRadius: "3px",
                      fontWeight: 600,
                    }}
                    title="Facility hasn't acknowledged receipt of this invoice yet."
                  >
                    Awaiting ack
                  </span>
                )}
                <button
                  style={styles.button}
                  onClick={() => exportInvoice(r)}
                  title="Export PDF"
                >
                  PDF
                </button>
                {canSeeMargin && (
                  <button
                    style={styles.button}
                    onClick={() => exportOfficeCopy(r)}
                    title="Internal Office Copy — cost vs charge vs margin per line. NOT for the customer."
                  >
                    Office Copy
                  </button>
                )}
                <button
                  style={styles.button}
                  onClick={() => onSelectJob && onSelectJob(r.job)}
                  title="Open the job this invoice belongs to"
                >
                  View Job
                </button>
              </div>
              {showRecordPayment && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    style={{
                      ...styles.button,
                      background: "#1a7f37",
                      color: "white",
                      margin: 0,
                    }}
                    onClick={() => setPaymentTarget(r)}
                    title="Record a payment against this invoice. Marks acknowledged automatically."
                  >
                    Record payment
                  </button>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      <PaymentEntryModal
        open={!!paymentTarget}
        title="Record customer payment"
        subtitle={
          paymentTarget
            ? `${paymentTarget.invoiceNumber || "(no invoice #)"} — ${
                paymentTarget.customerName || ""
              }`
            : ""
        }
        billTotal={paymentTarget?.grandTotal || 0}
        alreadyPaid={paymentTarget?.paidAmount || 0}
        onCancel={() => setPaymentTarget(null)}
        onConfirm={async (payment) => {
          if (!actions?.recordBillPayment) return;
          await actions.recordBillPayment(
            paymentTarget.jobId,
            paymentTarget.billId,
            payment
          );
          setPaymentTarget(null);
        }}
      />

      <PdfActionModal
        pending={pdfPending}
        title="Export Invoice"
        onPrint={() => {
          if (pdfPending) printPdf(pdfPending.doc);
          setPdfPending(null);
        }}
        onSave={async () => {
          if (pdfPending) await exportPdf(pdfPending.doc, pdfPending.filename);
          setPdfPending(null);
        }}
        onCancel={() => setPdfPending(null)}
      />
    </>
  );
}
